import type { CfoMaterializationRefreshInfo } from '../../../data/ServerApiClient'
import { useEffect, useState } from 'react'

function elapsedLabel(start: string | null | undefined, now: number): string | null {
  if (!start) return null
  const elapsed = now - Date.parse(start)
  if (!Number.isFinite(elapsed) || elapsed < 0) return null
  const seconds = Math.floor(elapsed / 1000)
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

function ActivityTime({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return <span>{label} <time dateTime={value}>{date.toLocaleString()}</time>.</span>
}

/** Task counts describe completed steps, never an estimate of remaining time. */
export function CfoMaterializationActivity({ refresh, active = true }: {
  refresh: CfoMaterializationRefreshInfo | null | undefined
  active?: boolean
}) {
  const [now, setNow] = useState(Date.now)
  useEffect(() => {
    if (!active) return
    const update = () => { if (document.visibilityState !== 'hidden') setNow(Date.now()) }
    const timer = window.setInterval(update, 2000)
    document.addEventListener('visibilitychange', update)
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', update) }
  }, [active])
  const activity = refresh?.activity
  const total = activity?.total_tasks ?? null
  const completed = activity ? activity.succeeded_tasks + activity.skipped_tasks : null
  const unsuccessful = activity ? activity.failed_tasks + activity.cancelled_tasks : 0
  const labels = [...new Set(activity?.current_tasks.map((task) => task.label) || [])]
  let detail = 'Waiting for progress details.'
  if (activity) {
    if (activity.running_tasks > 0) {
      detail = labels.length ? labels.join(' · ') : 'Processing reports.'
      if (activity.current_tasks_overflow) detail += ' · More steps running.'
    } else if (unsuccessful > 0) {
      detail = `${unsuccessful} ${unsuccessful === 1 ? 'step needs' : 'steps need'} attention.`
    } else if (total === 0) {
      detail = active ? 'Waiting for report steps to be prepared.' : 'No report steps were recorded.'
    } else if (completed === total) {
      detail = active ? 'Steps finished; verifying publication.' : 'Steps finished; publication needs attention.'
    } else {
      detail = active ? 'Waiting for a worker to start the next step.' : 'Remaining steps are not running.'
    }
  }
  const count = total !== null && total > 0 ? `${completed} of ${total} steps complete` : null
  const accessible = [count, detail].filter(Boolean).join('. ')
  const elapsed = elapsedLabel(refresh?.started_at, now)
  return <div className={`cfo-materialization-activity${active ? '' : ' is-inactive'}`}>
    {active ? <div className="cfo-materialization-progress is-indeterminate" role="progressbar"
      aria-label="Report update activity" aria-valuetext={accessible}>
      <span />
    </div> : null}
    <em>{count ? `${count}. ` : ''}{detail}</em>
    {elapsed || refresh?.last_worker_contact_at ? <div className="cfo-materialization-timing">
      {elapsed ? <span>Elapsed {elapsed}.</span> : null}
      <ActivityTime label="Last worker contact at" value={refresh?.last_worker_contact_at} />
    </div> : null}
    {activity?.skipped_tasks || activity?.last_activity_at || activity?.observed_at ? <details className="cfo-materialization-details">
      <summary>Activity details</summary>
      {activity?.skipped_tasks ? <span>{activity.skipped_tasks} {activity.skipped_tasks === 1 ? 'step was' : 'steps were'} not needed.</span> : null}
      <ActivityTime label="Last worker activity at" value={activity?.last_activity_at} />
      <ActivityTime label="Status checked at" value={activity?.observed_at} />
    </details> : null}
  </div>
}
